// src/components/agenda/ModalBloquearHorario.js
// Ventana para bloquear la agenda (vacaciones, festivo, formación, una tarde libre).
// Recepción y dirección pueden bloquear a cualquier profesional o a toda la
// clínica; un médico solo su propia agenda (el servidor lo vuelve a comprobar).
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Modal } from '../common/ui';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { MOTIVOS_BLOQUEO } from '../../hooks/useBloqueos';

const TODA_LA_CLINICA = 'clinica';

const estadoInicial = (fecha, profesionalPorDefecto) => {
    const dia = format(fecha || new Date(), 'yyyy-MM-dd');
    return {
        profesional: profesionalPorDefecto,
        motivo: 'vacaciones',
        diaCompleto: true,
        fechaInicio: dia,
        horaInicio: '15:00',
        fechaFin: dia,
        horaFin: '21:00',
        nota: ''
    };
};

const aFecha = (dia, hora) => {
    const [a, m, d] = dia.split('-').map(Number);
    const [h, min] = hora.split(':').map(Number);
    return new Date(a, m - 1, d, h, min, 0, 0);
};

const claseCampo = 'w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400';
const claseEtiqueta = 'block text-[11px] font-black uppercase tracking-wide text-slate-500 mb-1.5';

export default function ModalBloquearHorario({ open, onClose, onSuccess, fecha, medicos = [], esAdmin, user }) {
    const { showToast } = useToast();
    const profesionalPorDefecto = esAdmin ? TODA_LA_CLINICA : String(user?.id || '');
    const [form, setForm] = useState(() => estadoInicial(fecha, profesionalPorDefecto));
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setForm(estadoInicial(fecha, profesionalPorDefecto));
            setError('');
        }
    }, [open, fecha, profesionalPorDefecto]);

    const cambiar = (campo) => (e) => {
        const valor = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm(f => ({ ...f, [campo]: valor }));
    };

    const guardar = async () => {
        if (!form.fechaInicio || !form.fechaFin) return setError('Indica las fechas del bloqueo.');

        let inicio;
        let fin;
        if (form.diaCompleto) {
            inicio = aFecha(form.fechaInicio, '00:00');
            fin = aFecha(form.fechaFin, '00:00');
            fin.setDate(fin.getDate() + 1); // hasta el final del último día
        } else {
            inicio = aFecha(form.fechaInicio, form.horaInicio);
            fin = aFecha(form.fechaFin, form.horaFin);
        }
        if (!(fin > inicio)) return setError('El fin debe ser posterior al inicio.');

        setGuardando(true);
        setError('');
        try {
            const res = await api.post('/bloqueos', {
                profesional: form.profesional === TODA_LA_CLINICA ? null : form.profesional,
                inicio: inicio.toISOString(),
                fin: fin.toISOString(),
                motivo: form.motivo,
                nota: form.nota.trim()
            });
            const afectadas = res?.citasAfectadas || 0;
            if (afectadas > 0) {
                showToast(`Horario bloqueado. Atención: ${afectadas === 1 ? 'queda 1 cita pendiente' : `quedan ${afectadas} citas pendientes`} dentro del periodo; revísalas.`, 'warning');
            } else {
                showToast('Horario bloqueado', 'success');
            }
            onSuccess?.(res);
            onClose?.();
        } catch (err) {
            setError(err?.message || 'No se pudo bloquear el horario.');
        } finally {
            setGuardando(false);
        }
    };

    const footer = (
        <>
            <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100 transition-colors">
                Cancelar
            </button>
            <button
                type="button"
                onClick={guardar}
                disabled={guardando}
                className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 disabled:opacity-60 transition-colors"
            >
                {guardando ? 'Guardando...' : 'Bloquear horario'}
            </button>
        </>
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            titulo="Bloquear horario"
            subtitulo="En este periodo no se podrán dar citas"
            footer={footer}
        >
            <div className="space-y-4">
                <div>
                    <label className={claseEtiqueta} htmlFor="bloqueo-profesional">Agenda</label>
                    <select id="bloqueo-profesional" className={claseCampo} value={form.profesional} onChange={cambiar('profesional')} disabled={!esAdmin}>
                        {esAdmin && <option value={TODA_LA_CLINICA}>Toda la clínica</option>}
                        {(esAdmin ? medicos : medicos.filter(m => String(m.id) === String(user?.id))).map(m => (
                            <option key={m.id} value={String(m.id)}>{m.nombre} {m.apellidos}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className={claseEtiqueta} htmlFor="bloqueo-motivo">Motivo</label>
                    <select id="bloqueo-motivo" className={claseCampo} value={form.motivo} onChange={cambiar('motivo')}>
                        {MOTIVOS_BLOQUEO.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                    </select>
                </div>

                <label className="flex items-center gap-2.5 text-sm font-bold text-slate-700 cursor-pointer select-none">
                    <input type="checkbox" checked={form.diaCompleto} onChange={cambiar('diaCompleto')} className="w-4 h-4 rounded border-slate-300" />
                    Días completos
                </label>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className={claseEtiqueta} htmlFor="bloqueo-fecha-inicio">Desde</label>
                        <input id="bloqueo-fecha-inicio" type="date" className={claseCampo} value={form.fechaInicio} onChange={cambiar('fechaInicio')} />
                    </div>
                    <div>
                        <label className={claseEtiqueta} htmlFor="bloqueo-fecha-fin">Hasta</label>
                        <input id="bloqueo-fecha-fin" type="date" className={claseCampo} value={form.fechaFin} onChange={cambiar('fechaFin')} />
                    </div>
                    {!form.diaCompleto && (
                        <>
                            <div>
                                <label className={claseEtiqueta} htmlFor="bloqueo-hora-inicio">Hora de inicio</label>
                                <input id="bloqueo-hora-inicio" type="time" step="900" className={claseCampo} value={form.horaInicio} onChange={cambiar('horaInicio')} />
                            </div>
                            <div>
                                <label className={claseEtiqueta} htmlFor="bloqueo-hora-fin">Hora de fin</label>
                                <input id="bloqueo-hora-fin" type="time" step="900" className={claseCampo} value={form.horaFin} onChange={cambiar('horaFin')} />
                            </div>
                        </>
                    )}
                </div>

                <div>
                    <label className={claseEtiqueta} htmlFor="bloqueo-nota">Nota (opcional)</label>
                    <input id="bloqueo-nota" type="text" maxLength={200} className={claseCampo} value={form.nota} onChange={cambiar('nota')} placeholder="Ej.: congreso en Madrid" />
                </div>

                <p className="text-xs font-semibold text-slate-400">
                    Las citas que ya estén dadas dentro del periodo no se anulan: se te avisará para que las revises.
                </p>

                {error && (
                    <div className="px-3 py-2.5 rounded-xl bg-rose-50 border border-rose-100 text-sm font-bold text-rose-700">{error}</div>
                )}
            </div>
        </Modal>
    );
}
